import { Alert } from '@/shared/components/rebranded/Alert/Alert';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { useFlag } from '@unleash/proxy-client-react';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Route } from 'react-router-dom';
import styled from 'styled-components';
import {
  AdvisorLoginForm,
  LoggedInAsAdvisor,
  startAdvisorSession
} from '../advisor';
import { CorporateContextPath } from '../CorporateContext';
import '../form-generator/resolveFieldOrWidget/importWidgets';
import AppRouter from '../shared/components/AppRouter/AppRouter';
import ProtectedRoute from '../shared/components/AppRouter/ProtectedRoute';
import AuthTimeoutHandler from '../shared/components/AuthTimeoutHandler';
import { DowntimeStatus } from '../shared/components/DownTimeStatus';
import ErrorBoundary from '../shared/components/ErrorBoundary';
import Redirect from '../shared/components/Redirect';
import useDowntime from '../shared/hooks/useDowntime/useDowntime';
import { FEATURE_TOGGLE } from '../utils/unleash';

const Travel = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/travel')
);
const HouseContents = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/house-contents')
);
const Animals = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/animals')
);
const Vehicle = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/vehicle')
);
const CorporateContents = lazy(
  () => import('../pages/claimForms/claimFormsCorporate/corporate-contents')
);
const CorporateVehicle = lazy(
  () => import('../pages/claimForms/claimFormsCorporate/vehicle')
);
const CorporateBuilding = lazy(
  () => import('../pages/claimForms/claimFormsCorporate/building')
);
const CorporateProfessionalLiability = lazy(
  () => import('../pages/claimForms/claimFormsCorporate/professional-liability')
);
const CorporateLiability = lazy(
  () => import('../pages/claimForms/claimFormsCorporate/liability')
);
const CorporateProject = lazy(
  () => import('../pages/claimForms/claimFormsCorporate/project')
);
const CorporateTransport = lazy(
  () => import('../pages/claimForms/claimFormsCorporate/transport')
);
const Trailer = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/trailer')
);
const Boat = lazy(() => import('../pages/claimForms/claimFormsPrivate/boat'));
const LegalAid = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/legal-aid')
);
const Valuable = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/valuable')
);
const Building = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/building')
);
const Rental = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/rental')
);
const ElectricScooter = lazy(
  () => import('../pages/claimForms/claimFormsPrivate/electric-scooter')
);
const NewClaimFromPolicy = lazy(
  () =>
    import(
      '../pages/claimForms/claimFormsPrivate/new-claim-from-policy/NewClaimFromPolicy'
    )
);
const NewClaimFromCorporatePolicy = lazy(
  () =>
    import(
      '../pages/claimForms/claimFormsPrivate/new-claim-from-policy/NewClaimFromCorporatePolicy'
    )
);
const FollowUp = lazy(() => import('../pages/trackMyCase/follow-up'));
const ClaimStatus = lazy(() => import('../pages/trackMyCase'));
const ClaimsOverview = lazy(() => import('../pages/claimOverview'));
const PrintableClaim = lazy(
  () => import('../pages/trackMyCase/printableClaim/PrintableClaim')
);

const App = () => {
  const maintenance = useFlag(FEATURE_TOGGLE.MAINTENANCE);
  const { getText } = useTranslations();
  const serviceStatus = useDowntime();

  const isMaintenance =
    maintenance && !window.location.href.endsWith('?ignoreMaintenance');

  if (isMaintenance) {
    return (
      <div className="App">
        <AlertWrapper>
          <Alert
            fluid
            variant="warning"
            title={getText('maintenance.title')}
            description={getText('maintenance.description')}
          />
        </AlertWrapper>
      </div>
    );
  }

  if (serviceStatus?.status === 'down' && serviceStatus.isEssential) {
    return (
      <div className="App">
        <AlertWrapper>
          <Alert
            fluid
            variant="warning"
            title={serviceStatus.title}
            description={serviceStatus.message}
          />
        </AlertWrapper>
      </div>
    );
  }

  if (window.location.pathname === '/meld-skade/radgiver') {
    return (
      <ErrorBoundary>
        <AdvisorLoginForm
          onSubmit={({ cmId, claimId, printToPDF }) => {
            startAdvisorSession(cmId);

            const basePath = '/meld-skade/skadesaker';

            if (claimId && printToPDF) {
              window.location.replace(`${basePath}/utskrift/${claimId}`);
              return;
            }
            if (claimId) {
              window.location.replace(`${basePath}/${claimId}`);

              return;
            }

            window.location.pathname = basePath;
          }}
        />
      </ErrorBoundary>
    );
  }

  return (
    <div className="container">
      <LoggedInAsAdvisor />
      <main className="App">
        <ErrorBoundary>
          <AlertWrapper>
            <DowntimeStatus serviceStatus={serviceStatus} />
          </AlertWrapper>
          <AuthTimeoutHandler />
          <Suspense fallback={<div />}>
            <BrowserRouter basename="/meld-skade">
              <AppRouter>
                <Route path="/" element={<ProtectedRoute />}>
                  <Route index element={<Redirect to="/skadesaker" />} />
                  <Route path="/skadesaker" element={<ClaimsOverview />} />
                  <Route
                    path="/oppfolging/:claimId/"
                    element={<Redirect to="1" />}
                  />
                  <Route path="/oppfolging/:claimId/*" element={<FollowUp />} />
                  <Route
                    path="/skadesaker/:claimId/*"
                    element={<ClaimStatus />}
                  />
                  <Route
                    path="/skadesaker/utskrift/:claimId"
                    element={<PrintableClaim />}
                  />

                  <Route
                    path="/"
                    element={<CorporateContextPath isCorporate={false} />}
                  >
                    <Route path="/reise/*" element={<Travel />} />
                    <Route path="/innbo/*" element={<HouseContents />} />

                    <Route path="/verdigjenstand/*" element={<Valuable />} />
                    <Route path="/hund-og-katt/*" element={<Animals />} />
                    <Route path="/rettshjelp/*" element={<LegalAid />} />
                    <Route path="/kjoretoy/*" element={<Vehicle />} />
                    <Route
                      path="/campingvogn-og-tilhenger/*"
                      element={<Trailer />}
                    />
                    <Route path="/hus-hytte/*" element={<Building />} />
                    <Route path="/bat/*" element={<Boat />} />
                    <Route path="/utleie/*" element={<Rental />} />
                    <Route
                      path="/elsparkesykkel/*"
                      element={<ElectricScooter />}
                    />
                    <Route
                      path="/fra-polise/:policyNumber/*"
                      element={<NewClaimFromPolicy />}
                    />
                  </Route>
                  <Route
                    path="bedrift"
                    element={<CorporateContextPath isCorporate />}
                  >
                    <Route index element={<Redirect to="/skadesaker" />} />
                    <Route
                      path="skadesaker"
                      element={<Redirect to="/skadesaker" />}
                    />
                    <Route
                      path="skadesaker/:claimId"
                      element={<Redirect to="/skadesaker?bedrift=true" />}
                    />

                    <Route path="kjoretoy/*" element={<CorporateVehicle />} />
                    <Route
                      path="borettslag-og-naeringsbygg/*"
                      element={<CorporateBuilding />}
                    />
                    <Route path="bat/*" element={<Boat />} />
                    <Route
                      path="kontor-butikk-bedrift/*"
                      element={<CorporateContents />}
                    />
                    <Route
                      path="profesjonsansvar/*"
                      element={<CorporateProfessionalLiability />}
                    />
                    <Route path="ansvar/*" element={<CorporateLiability />} />
                    <Route path="prosjekt/*" element={<CorporateProject />} />
                    <Route
                      path="transport/*"
                      element={<CorporateTransport />}
                    />
                    <Route path="reise/*" element={<Travel />} />
                    <Route
                      path="campingvogn-og-tilhenger/*"
                      element={<Trailer />}
                    />
                    <Route path="hus-hytte/*" element={<Building />} />
                    <Route path="innbo/*" element={<HouseContents />} />
                    <Route path="verdigjenstand/*" element={<Valuable />} />
                    <Route
                      path="fra-polise"
                      element={<NewClaimFromCorporatePolicy />}
                    />
                  </Route>
                  <Route path="*" element={<Redirect to="/skadesaker" />} />
                </Route>
              </AppRouter>
            </BrowserRouter>
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
};

const AlertWrapper = styled.div`
  margin: 32px 0 32px;

  & > div:not(:first-child) {
    margin-top: 24px;
  }
`;

export default App;
